import React from 'react';
import PropTypes from 'prop-types';
import { isMusicJob } from 'helpers/HelperFunctions';
import styles from './UserBrowseProjectCard.module.scss';

const PORTFOLIO_PROJECTS_HEADING = 'Portfolio projects';

/** Typesense `projects[].type` — title + description only, no thumbnail. */
export const TEXT_PROJECT_TYPE = 'text';

/** Typesense `projects[].type` — audio / music portfolio item. */
export const AUDIO_PROJECT_TYPE = 'audio';

/**
 * Width of each project tile in the horizontal scroller, as a fraction of the
 * row (matches previous “four across” layout). Used in SCSS only.
 */
export const VISIBLE_PROJECT_SLOTS = 4;

/**
 * Safety cap on how many projects we render (Typesense payloads can be large).
 */
export const MAX_BROWSE_PROJECTS_RENDERED = 40;

/**
 * Music browse layout: show this many rows before the list becomes vertically scrollable.
 * Keep in sync with `$music-projects-visible-rows` in UserBrowseProjectCard.module.scss.
 */
export const MUSIC_PROJECTS_VISIBLE_ROWS = 2;

/**
 * @param {string} [type]
 * @returns {boolean}
 */
export function isAudioProjectType(type) {
  if (type == null || String(type).trim() === '') {
    return false;
  }
  return String(type).trim().toLowerCase() === AUDIO_PROJECT_TYPE;
}

/**
 * @param {string} [type]
 * @returns {boolean}
 */
export function isTextProjectType(type) {
  if (type == null || String(type).trim() === '') {
    return false;
  }
  return String(type).trim().toLowerCase() === TEXT_PROJECT_TYPE;
}

/**
 * @param {unknown} raw Role from Typesense (`string` or `{ name }`).
 * @returns {string}
 */
function roleLabelFromHit(raw) {
  if (typeof raw === 'string') {
    return String(raw).trim();
  }
  if (raw && raw.name != null) {
    return String(raw.name).trim();
  }
  return '';
}

/**
 * True if any hit `roles` entry is a music job per {@link isMusicJob}.
 *
 * @param {Record<string, unknown>|null|undefined} hit
 * @returns {boolean}
 */
export function browseHitHasMusicalRole(hit) {
  if (!hit || typeof hit !== 'object' || !Array.isArray(hit.roles)) {
    return false;
  }
  return hit.roles.some((r) => {
    const name = roleLabelFromHit(r);
    return name !== '' && isMusicJob(name);
  });
}

/**
 * Normalizes Typesense `projects` for the browse card.
 * With `musicLayout`, only `type: audio` items are returned (music profiles).
 *
 * @param {Record<string, unknown>|null|undefined} hit
 * @param {{ musicLayout?: boolean }} [options]
 * @returns {Array<{ title: string; description: string; thumbnailUrl: string; type: string }>}
 */
export function browseProjectsFromHit(hit, options = {}) {
  const { musicLayout = false } = options;
  if (!hit || typeof hit !== 'object') {
    return [];
  }
  const raw = hit.projects;
  if (!Array.isArray(raw)) {
    return [];
  }
  let list = raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const type =
        entry.type != null && String(entry.type).trim() !== ''
          ? String(entry.type).trim()
          : '';
      const title =
        entry.title != null && String(entry.title).trim() !== ''
          ? String(entry.title).trim()
          : '';
      const description =
        entry.description != null && String(entry.description).trim() !== ''
          ? String(entry.description).trim()
          : '';
      const thumbnailUrl =
        entry.thumbnail_url != null ? String(entry.thumbnail_url).trim() : '';
      if (!title && !description && !thumbnailUrl) {
        return null;
      }
      return { title, description, thumbnailUrl, type };
    })
    .filter(Boolean);

  if (musicLayout) {
    list = list.filter((p) => isAudioProjectType(p.type));
  }

  return list.slice(0, MAX_BROWSE_PROJECTS_RENDERED);
}

function UserBrowseMusicProjectRow({ thumbnailUrl, title, description }) {
  const hasThumb = thumbnailUrl !== '';

  return (
    <div className={styles.musicProjectCard}>
      <div className={styles.musicProjectThumbWrap}>
        {hasThumb ? (
          <img
            className={styles.musicProjectThumb}
            src={thumbnailUrl}
            alt={title || ''}
            loading="lazy"
          />
        ) : (
          <div className={styles.musicProjectThumbPlaceholder} aria-hidden />
        )}
      </div>
      <div className={styles.musicProjectBody}>
        {title ? <p className={styles.musicProjectTitle}>{title}</p> : null}
        {description ? (
          <p className={styles.musicProjectDescription}>{description}</p>
        ) : null}
      </div>
    </div>
  );
}

UserBrowseMusicProjectRow.propTypes = {
  thumbnailUrl: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
};

UserBrowseMusicProjectRow.defaultProps = {
  thumbnailUrl: '',
  title: '',
  description: '',
};

function UserBrowseProjectCard({ thumbnailUrl, title, description, type }) {
  const hasThumb = thumbnailUrl !== '';
  const text = isTextProjectType(type);

  if (text) {
    return (
      <div className={styles.textTile}>
        {title ? <p className={styles.textTileTitle}>{title}</p> : null}
        {description ? (
          <p className={styles.textTileDescription}>{description}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.thumbTile}>
      {hasThumb ? (
        <img
          className={styles.thumbnail}
          src={thumbnailUrl}
          alt={title || ''}
          loading="lazy"
        />
      ) : (
        <div className={styles.thumbnailPlaceholder} aria-hidden />
      )}
    </div>
  );
}

UserBrowseProjectCard.propTypes = {
  thumbnailUrl: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string,
};

UserBrowseProjectCard.defaultProps = {
  thumbnailUrl: '',
  title: '',
  description: '',
  type: '',
};

/**
 * @param {'default'|'music'} variant `music`: full-width audio rows; `default`: thumbnail strip.
 */
export function UserBrowseProjectsList({ projects, variant }) {
  const list = Array.isArray(projects) ? projects : [];
  if (!list.length) {
    return null;
  }

  if (variant === 'music') {
    const musicListClass = [
      styles.projectsListMusic,
      list.length > MUSIC_PROJECTS_VISIBLE_ROWS &&
        styles.projectsListMusicScrollable,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={styles.projectsSection} data-browse-portfolio="">
        <h3 className={styles.projectsHeading}>{PORTFOLIO_PROJECTS_HEADING}</h3>
        <div className={musicListClass}>
          {list.map((p, index) => (
            <UserBrowseMusicProjectRow
              key={`${p.thumbnailUrl || 'a'}-${index}`}
              thumbnailUrl={p.thumbnailUrl}
              title={p.title}
              description={p.description}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.projectsSection} data-browse-portfolio="">
      <h3 className={styles.projectsHeading}>{PORTFOLIO_PROJECTS_HEADING}</h3>
      <div className={styles.projectsScroll}>
        {list.map((p, index) => (
          <UserBrowseProjectCard
            key={`${p.thumbnailUrl || 'p'}-${p.type || ''}-${index}`}
            thumbnailUrl={p.thumbnailUrl}
            title={p.title}
            description={p.description}
            type={p.type}
          />
        ))}
      </div>
    </div>
  );
}

UserBrowseProjectsList.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      thumbnailUrl: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  variant: PropTypes.oneOf(['default', 'music']),
};

UserBrowseProjectsList.defaultProps = {
  projects: [],
  variant: 'default',
};

export default UserBrowseProjectCard;
